import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useRoute } from "wouter";
import { ClientForm } from "@/components/admin/billing/client-form";
import { ServiceForm } from "@/components/admin/billing/service-form";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, Pencil, Plus, Zap, History, UserCog } from "lucide-react";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import type { Client, ClientService, AuditLog } from "@db/schema";

interface HighLevelLinkRow {
  id: string;
  opportunity: {
    name: string;
    stageName: string | null;
  };
  contract: {
    id: string;
    name: string;
  } | null;
  createdAt: string;
}

interface ClientFormData {
  name: string;
  contactEmail?: string;
  phone?: string;
  billingAddress?: string;
  billingEmails?: string;
  vatNumber?: string;
  taxExempt: boolean;
  primaryDomain?: string;
}

interface AccountManagerOption {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface ClientContactRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
  jobTitle: string | null;
  notes: string | null;
  isPrimary: boolean;
  receivesBillingEmails: boolean;
  isActive: boolean;
  userId: string | null;
  createdAt: string;
}

export default function ClientDetailPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/clients/:id");
  const clientId = params?.id;

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addServiceDialogOpen, setAddServiceDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ClientContactRow | null>(null);
  const [contactForm, setContactForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    jobTitle: "",
    notes: "",
    isPrimary: false,
    receivesBillingEmails: false,
    isActive: true,
  });

  const {
    data: client,
    isLoading: clientLoading,
  } = useQuery<Client>({
    queryKey: ["/api/billing/clients", clientId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!clientId,
  });

  const { data: services, isLoading: servicesLoading } = useQuery<
    ClientService[]
  >({
    queryKey: [`/api/billing/client-services?clientId=${clientId}`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!clientId,
  });

  const { data: activity, isLoading: activityLoading } = useQuery<AuditLog[]>({
    queryKey: [`/api/billing/clients/${clientId}/activity`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!clientId,
  });

  const { data: accountManagers } = useQuery<AccountManagerOption[]>({
    queryKey: ["/api/commercial/account-managers"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: contacts, isLoading: contactsLoading } = useQuery<ClientContactRow[]>({
    queryKey: [`/api/billing/clients/${clientId}/contacts`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!clientId,
  });

  const { data: highLevelLinks, isLoading: highLevelLinksLoading } = useQuery<
    HighLevelLinkRow[]
  >({
    queryKey: [`/api/clients/${clientId}/highlevel-links`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!clientId,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ClientFormData) => {
      const payload = {
        ...data,
        billingEmails: data.billingEmails
          ? data.billingEmails
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean)
          : [],
      };
      const res = await apiRequest(
        "PATCH",
        `/api/billing/clients/${clientId}`,
        payload,
      );
      return (await res.json()) as Client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/billing/clients", clientId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/clients"] });
      setEditDialogOpen(false);
      toast({ title: "Client updated successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update client",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const createServiceMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiRequest("POST", "/api/billing/client-services", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (q) => (q.queryKey[0] as string)?.startsWith("/api/billing/"),
      });
      setAddServiceDialogOpen(false);
      toast({ title: "Service added successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to add service",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const accountManagerMutation = useMutation({
    mutationFn: async (accountManagerUserId: string | null) => {
      const res = await apiRequest(
        "PATCH",
        `/api/clients/${clientId}/account-manager`,
        { accountManagerUserId },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (q) =>
          typeof q.queryKey[0] === "string" &&
          (q.queryKey[0] as string).startsWith("/api/billing/clients"),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Account manager updated" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update account manager",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const saveContactMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...contactForm,
        firstName: contactForm.firstName || null,
        lastName: contactForm.lastName || null,
        phone: contactForm.phone || null,
        jobTitle: contactForm.jobTitle || null,
        notes: contactForm.notes || null,
      };

      if (editingContact) {
        const res = await apiRequest(
          "PATCH",
          `/api/billing/clients/${clientId}/contacts/${editingContact.id}`,
          payload,
        );
        return res.json();
      }

      const res = await apiRequest(
        "POST",
        `/api/billing/clients/${clientId}/contacts`,
        payload,
      );
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [`/api/billing/clients/${clientId}/contacts`],
      });
      await queryClient.invalidateQueries({
        queryKey: ["/api/billing/clients", clientId],
      });
      setContactDialogOpen(false);
      setEditingContact(null);
      setContactForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        jobTitle: "",
        notes: "",
        isPrimary: false,
        receivesBillingEmails: false,
        isActive: true,
      });
      toast({ title: editingContact ? "Contact updated" : "Contact created" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to save contact",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await apiRequest("DELETE", `/api/billing/clients/${clientId}/contacts/${contactId}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [`/api/billing/clients/${clientId}/contacts`],
      });
      await queryClient.invalidateQueries({
        queryKey: ["/api/billing/clients", clientId],
      });
      toast({ title: "Contact deleted" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to delete contact",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const { data: clientRoles = [] } = useQuery<
    Array<{ id: string; name: string; audience: string; isSystem: boolean; slug?: string }>
  >({
    queryKey: ["/api/billing/client-portal-roles"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const assignableClientRoles = clientRoles.filter(
    (role) => role.slug !== "client-partner",
  );

  const [portalRoleDialogOpen, setPortalRoleDialogOpen] = useState(false);
  const [portalContact, setPortalContact] = useState<ClientContactRow | null>(
    null,
  );
  const [selectedPortalRoleId, setSelectedPortalRoleId] = useState<string>("");

  const togglePortalMutation = useMutation({
    mutationFn: async ({
      contact,
      roleId,
    }: {
      contact: ClientContactRow;
      roleId?: string;
    }) => {
      if (contact.userId) {
        await apiRequest(
          "POST",
          `/api/billing/clients/${clientId}/contacts/${contact.id}/disable-portal`,
        );
        return;
      }
      await apiRequest(
        "POST",
        `/api/billing/clients/${clientId}/contacts/${contact.id}/enable-portal`,
        roleId ? { roleId } : {},
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [`/api/billing/clients/${clientId}/contacts`],
      });
      setPortalRoleDialogOpen(false);
      setPortalContact(null);
      toast({ title: "Portal access updated" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update portal access",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const openEnablePortal = (contact: ClientContactRow) => {
    const defaultRole =
      assignableClientRoles.find((role) => role.slug === "client") ??
      assignableClientRoles.find((role) => role.name === "Client") ??
      assignableClientRoles[0];
    setPortalContact(contact);
    setSelectedPortalRoleId(defaultRole?.id ?? "");
    setPortalRoleDialogOpen(true);
  };

  function getFormDefaults(c: Client) {
    return {
      name: c.name,
      contactEmail: c.contactEmail ?? "",
      phone: c.phone ?? "",
      billingAddress: c.billingAddress ?? "",
      billingEmails: (c.billingEmails ?? []).join(", "),
      vatNumber: c.vatNumber ?? "",
      taxExempt: c.taxExempt,
      primaryDomain: c.primaryDomain ?? "",
    };
  }

  const serviceColumns: ColumnDef<ClientService>[] = [
    {
      key: "name",
      header: "Service",
      cell: (row) => (
        <span className="font-medium">
          {row.customName || "Service"}
        </span>
      ),
    },
    {
      key: "price",
      header: "Price (ex. VAT)",
      cell: (row) =>
        row.price ? formatCurrency(row.price, row.currency) : "--",
    },
    {
      key: "recurrence",
      header: "Recurrence",
      cell: (row) => (
        <span className="capitalize">{row.recurrence}</span>
      ),
    },
    {
      key: "nextRenewalDate",
      header: "Next Renewal",
      cell: (row) => formatDate(row.nextRenewalDate),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <StatusBadge type="service" status={row.status} />,
    },
  ];

  const activityColumns: ColumnDef<AuditLog>[] = [
    {
      key: "action",
      header: "Action",
      cell: (row) => (
        <span className="font-medium capitalize">
          {row.action.replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "entityType",
      header: "Entity",
      cell: (row) => (
        <span className="capitalize">
          {row.entityType.replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Date",
      cell: (row) => formatDateTime(row.createdAt),
    },
  ];

  const highLevelColumns: ColumnDef<HighLevelLinkRow>[] = [
    {
      key: "opportunity",
      header: "Opportunity",
      cell: (row) => (
        <div>
          <div className="font-medium">{row.opportunity.name}</div>
          <div className="text-xs text-muted-foreground">
            {row.opportunity.stageName || "Unknown stage"}
          </div>
        </div>
      ),
    },
    {
      key: "contract",
      header: "Contract",
      cell: (row) => row.contract?.name || "Not created",
    },
    {
      key: "createdAt",
      header: "Linked",
      cell: (row) => formatDateTime(row.createdAt),
    },
  ];

  const contactColumns: ColumnDef<ClientContactRow>[] = [
    {
      key: "name",
      header: "Contact",
      cell: (row) => (
        <div>
          <p className="font-medium">
            {[row.firstName, row.lastName].filter(Boolean).join(" ") || row.email}
          </p>
          <p className="text-xs text-muted-foreground">{row.email}</p>
        </div>
      ),
    },
    {
      key: "meta",
      header: "Details",
      cell: (row) => (
        <div className="text-sm text-muted-foreground">
          <p>{row.jobTitle || "No job title"}</p>
          <p>{row.phone || "No phone number"}</p>
        </div>
      ),
    },
    {
      key: "flags",
      header: "Flags",
      cell: (row) => (
        <div className="flex flex-wrap gap-2">
          {row.isPrimary && <Badge>Primary</Badge>}
          {row.receivesBillingEmails && <Badge variant="secondary">Billing</Badge>}
          {row.userId && <Badge variant="outline">Portal</Badge>}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setEditingContact(row);
              setContactForm({
                firstName: row.firstName ?? "",
                lastName: row.lastName ?? "",
                email: row.email,
                phone: row.phone ?? "",
                jobTitle: row.jobTitle ?? "",
                notes: row.notes ?? "",
                isPrimary: row.isPrimary,
                receivesBillingEmails: row.receivesBillingEmails,
                isActive: row.isActive,
              });
              setContactDialogOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              row.userId
                ? togglePortalMutation.mutate({ contact: row })
                : openEnablePortal(row)
            }
          >
            {row.userId ? "Disable Portal" : "Enable Portal"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => deleteContactMutation.mutate(row.id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  if (clientLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[200px] rounded-lg" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate("/clients")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Clients
        </Button>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Client not found</h2>
          <p className="text-muted-foreground mt-1">
            The requested client could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/clients")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-heading">{client.name}</h1>
            {client.contactEmail && (
              <p className="text-sm text-muted-foreground">
                {client.contactEmail}
              </p>
            )}
          </div>
        </div>
        <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
          <Pencil className="mr-2 h-4 w-4" />
          Edit Client
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts">Contacts ({contacts?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="services">
            Services ({services?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="crm">CRM Links</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base font-heading">
                  Client Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <InfoField label="Company Name" value={client.name} />
                  <InfoField
                    label="Contact Email"
                    value={client.contactEmail}
                  />
                  <InfoField label="Phone" value={client.phone} />
                  <InfoField
                    label="Primary Domain"
                    value={client.primaryDomain}
                  />
                  <InfoField label="VAT Number" value={client.vatNumber} />
                  <InfoField
                    label="Tax Exempt"
                    value={client.taxExempt ? "Yes" : "No"}
                  />
                  <InfoField
                    label="Billing Emails"
                    value={
                      client.billingEmails?.length
                        ? client.billingEmails.join(", ")
                        : null
                    }
                  />
                  <InfoField
                    label="Created"
                    value={formatDate(client.createdAt)}
                  />
                </div>
                {client.billingAddress && (
                  <div className="mt-4">
                    <InfoField
                      label="Billing Address"
                      value={client.billingAddress}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base font-heading">
                  Account Manager
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Contracts without an explicit owner inherit this client&apos;s account manager.
                </p>
                <Select
                  value={(client as Client & { accountManagerUserId?: string | null }).accountManagerUserId ?? "unassigned"}
                  onValueChange={(value) =>
                    accountManagerMutation.mutate(
                      value === "unassigned" ? null : value,
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {(accountManagers ?? []).map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                          user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="contacts">
          <div className="mb-4 flex justify-end">
            <Button
              onClick={() => {
                setEditingContact(null);
                setContactForm({
                  firstName: "",
                  lastName: "",
                  email: "",
                  phone: "",
                  jobTitle: "",
                  notes: "",
                  isPrimary: false,
                  receivesBillingEmails: false,
                  isActive: true,
                });
                setContactDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          </div>
          <DataTable
            columns={contactColumns}
            data={contacts}
            isLoading={contactsLoading}
            rowKey={(row) => row.id}
            emptyIcon={UserCog}
            emptyTitle="No contacts"
            emptyDescription="Add client contacts and optionally enable portal access."
          />
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setAddServiceDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Service
            </Button>
          </div>
          <DataTable
            columns={serviceColumns}
            data={services}
            isLoading={servicesLoading}
            rowKey={(row) => row.id}
            emptyIcon={Zap}
            emptyTitle="No services"
            emptyDescription="This client has no services yet."
            emptyAction={
              <Button onClick={() => setAddServiceDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Service
              </Button>
            }
          />
        </TabsContent>

        <TabsContent value="crm">
          <DataTable
            columns={highLevelColumns}
            data={highLevelLinks}
            isLoading={highLevelLinksLoading}
            rowKey={(row) => row.id}
            emptyIcon={History}
            emptyTitle="No HighLevel links"
            emptyDescription="Won opportunities linked to this client will appear here."
          />
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <DataTable
            columns={activityColumns}
            data={activity}
            isLoading={activityLoading}
            rowKey={(row) => row.id}
            emptyIcon={History}
            emptyTitle="No activity"
            emptyDescription="No activity recorded for this client yet."
          />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>
              Update the client details below.
            </DialogDescription>
          </DialogHeader>
          <ClientForm
            key={client.id}
            defaultValues={getFormDefaults(client)}
            onSubmit={(data) => updateMutation.mutate(data)}
            isPending={updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={addServiceDialogOpen} onOpenChange={setAddServiceDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Service for {client.name}</DialogTitle>
            <DialogDescription>
              Add a new service subscription for this client.
            </DialogDescription>
          </DialogHeader>
          <ServiceForm
            key={`add-svc-${clientId}`}
            fixedClientId={clientId}
            onSubmit={(data) => createServiceMutation.mutate(data)}
            isPending={createServiceMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingContact ? "Edit contact" : "Add contact"}</DialogTitle>
            <DialogDescription>
              Manage client contacts and billing recipients.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              saveContactMutation.mutate();
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contact-first-name">First name</Label>
                <Input
                  id="contact-first-name"
                  value={contactForm.firstName}
                  onChange={(event) =>
                    setContactForm((current) => ({
                      ...current,
                      firstName: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-last-name">Last name</Label>
                <Input
                  id="contact-last-name"
                  value={contactForm.lastName}
                  onChange={(event) =>
                    setContactForm((current) => ({
                      ...current,
                      lastName: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                required
                value={contactForm.email}
                onChange={(event) =>
                  setContactForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contact-phone">Phone</Label>
                <Input
                  id="contact-phone"
                  value={contactForm.phone}
                  onChange={(event) =>
                    setContactForm((current) => ({
                      ...current,
                      phone: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-job-title">Job title</Label>
                <Input
                  id="contact-job-title"
                  value={contactForm.jobTitle}
                  onChange={(event) =>
                    setContactForm((current) => ({
                      ...current,
                      jobTitle: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-notes">Notes</Label>
              <Input
                id="contact-notes"
                value={contactForm.notes}
                onChange={(event) =>
                  setContactForm((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
              />
            </div>

            <label className="flex items-center justify-between rounded-md border px-3 py-2">
              <span className="text-sm">Primary contact</span>
              <Checkbox
                checked={contactForm.isPrimary}
                onCheckedChange={(checked) =>
                  setContactForm((current) => ({
                    ...current,
                    isPrimary: checked === true,
                  }))
                }
              />
            </label>

            <label className="flex items-center justify-between rounded-md border px-3 py-2">
              <span className="text-sm">Receives billing emails</span>
              <Checkbox
                checked={contactForm.receivesBillingEmails}
                onCheckedChange={(checked) =>
                  setContactForm((current) => ({
                    ...current,
                    receivesBillingEmails: checked === true,
                  }))
                }
              />
            </label>

            <div className="flex justify-end">
              <Button type="submit" disabled={saveContactMutation.isPending}>
                {saveContactMutation.isPending ? "Saving..." : "Save contact"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={portalRoleDialogOpen}
        onOpenChange={(open) => {
          setPortalRoleDialogOpen(open);
          if (!open) setPortalContact(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enable portal access</DialogTitle>
            <DialogDescription>
              Choose which client role to assign. Permissions on the role control
              which portal sections this contact can access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Portal role</Label>
              <Select
                value={selectedPortalRoleId}
                onValueChange={setSelectedPortalRoleId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {assignableClientRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                      {role.isSystem ? " (system)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPortalRoleDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={
                  !portalContact ||
                  !selectedPortalRoleId ||
                  togglePortalMutation.isPending
                }
                onClick={() => {
                  if (!portalContact) return;
                  togglePortalMutation.mutate({
                    contact: portalContact,
                    roleId: selectedPortalRoleId,
                  });
                }}
              >
                {togglePortalMutation.isPending ? "Enabling..." : "Enable portal"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{value || "\u2014"}</dd>
    </div>
  );
}
